// import axios from "axios";

// import { loginRequest } from "../auth/msalConfig";

// const API_BASE_URL =
//   process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

// /* ================= AXIOS INSTANCE ================= */

// const apiClient = axios.create({
//   baseURL: API_BASE_URL,

//   timeout: 60000,
// });

// /* ================= MAIN API ================= */

// export const callApi = async (
//   instance,
//   accounts,
//   method,
//   url,
//   body = null,
//   options = {},
// ) => {
//   try {
//     const account = instance.getActiveAccount();

//     if (!account) {
//       throw new Error("No active account found");
//     }

//     /* ================= TOKEN ================= */

//     const tokenResponse = await instance.acquireTokenSilent({
//       ...loginRequest,
//       account,
//     });

//     /* ================= HEADERS ================= */

//     const headers = {
//       Authorization: `Bearer ${tokenResponse.accessToken}`,

//       ...(options.headers || {}),
//     };

//     /*
//       IMPORTANT:
//       Do NOT manually set multipart/form-data
//       Axios automatically handles boundary
//     */

//     if (!(body instanceof FormData)) {
//       headers["Content-Type"] = "application/json";
//     }

//     /* ================= REQUEST ================= */

//     /* ================= REQUEST ================= */

//     const requestConfig = {
//       method,

//       url,

//       params: options.params || {},

//       headers,

//       onUploadProgress: options.onUploadProgress,

//       responseType: options.responseType || "json",
//     };

//     /* ================= BODY ================= */

//     if (body !== null && body !== undefined) {
//       requestConfig.data = body;
//     }

//     const response = await apiClient(requestConfig);

//     /*
//       IMPORTANT:
//       Preserve existing compatibility
//     */

//     return {
//       data: response.data,
//     };
//   } catch (err) {
//     console.error("API call failed:", err);

//     /* ================= TOKEN RECOVERY ================= */

//     if (
//       err?.response?.status === 401 ||
//       err?.errorCode === "interaction_required"
//     ) {
//       try {
//         await instance.acquireTokenPopup(loginRequest);

//         return callApi(instance, accounts, method, url, body, options);
//       } catch (popupErr) {
//         console.error("Token popup failed:", popupErr);
//       }
//     }

//     /* ================= NORMALIZED ERROR ================= */

//     const error = new Error(
//       err?.response?.data?.message || err.message || "API request failed",
//     );

//     error.status = err?.response?.status;

//     error.response = err?.response?.data;

//     throw error;
//   }
// };

// export default apiClient;

import axios from "axios";
import { loginRequest } from "../auth/msalConfig";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

/* ================= AXIOS INSTANCE ================= */

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

/* ================= TOKEN CACHE ================= */

let popupInProgress = false;

let tokenPromise = null;

let cachedAccessToken = null;

let tokenExpiry = null;

/* ================= MAIN API ================= */
const getAccessToken = async (instance, accounts) => {
  /*
    Reuse cached token if still valid
  */

  const now = Date.now();

  if (cachedAccessToken && tokenExpiry && tokenExpiry - now > 2 * 60 * 1000) {
    return cachedAccessToken;
  }

  /*
    Prevent token acquisition storms
  */

  if (tokenPromise) {
    return tokenPromise;
  }

  const account = instance.getActiveAccount() || accounts?.[0];

  if (!account) {
    throw new Error("No active account found");
  }

  tokenPromise = instance
    .acquireTokenSilent({
      ...loginRequest,
      account,
    })
    .then((response) => {
      cachedAccessToken = response.accessToken;

      tokenExpiry = new Date(response.expiresOn).getTime();

      tokenPromise = null;

      return response.accessToken;
    })
    .catch((err) => {
      tokenPromise = null;

      throw err;
    });

  return tokenPromise;
};

export const callApi = async (
  instance,
  accounts,
  method,
  url,
  body = null,
  options = {},
) => {
  try {
    /* ================= ACCOUNT ================= */

    const accessToken = await getAccessToken(instance, accounts);

    /* ================= HEADERS ================= */

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      ...(options.headers || {}),
    };

    /*
      IMPORTANT:
      Axios automatically sets multipart boundaries
    */

    if (!(body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    /* ================= REQUEST CONFIG ================= */

    const requestConfig = {
      method,
      url,
      headers,
      params: options.params || {},
      data: body ?? undefined,
      responseType: options.responseType || "json",
      onUploadProgress: options.onUploadProgress,
      signal: options.signal,
      timeout: options.timeout || 60000,
    };

    /* ================= REQUEST ================= */

    const response = await apiClient(requestConfig);

    return {
      data: response.data,
    };
  } catch (err) {
    /*
    Ignore intentional request cancellations
  */

    if (
      err?.name === "CanceledError" ||
      err?.name === "AbortError" ||
      axios.isCancel(err)
    ) {
      throw new Error("Request cancelled");
    }

    console.error("API call failed:", err);

    /* ================= TOKEN RECOVERY ================= */

    const isUnauthorized = err?.response?.status === 401;

    const requiresInteraction = err?.errorCode === "interaction_required";

    /*
    Prevent infinite recursive retries
  */

    if ((isUnauthorized || requiresInteraction) && !options._retried) {
      try {
        /*
        Prevent popup storms
      */

        if (!popupInProgress) {
          popupInProgress = true;

          await instance.acquireTokenPopup(loginRequest);

          popupInProgress = false;
        }

        /*
        Retry ONLY ONCE
      */

        return callApi(instance, accounts, method, url, body, {
          ...options,
          _retried: true,
        });
      } catch (popupErr) {
        popupInProgress = false;

        console.error("Token popup recovery failed:", popupErr);
      }
    }

    /* ================= TIMEOUT ================= */

    if (err.code === "ECONNABORTED") {
      throw new Error("Request timeout. Please try again.");
    }

    /* ================= NORMALIZED ERROR ================= */

    const error = new Error(
      err?.response?.data?.message || err?.message || "API request failed",
    );

    error.status = err?.response?.status;

    error.response = err?.response?.data;

    throw error;
  }
};

export const authFetch = async (instance, accounts, url, options = {}) => {
  const accessToken = await getAccessToken(instance, accounts);

  const headers = {
    ...(options.headers || {}),

    Authorization: `Bearer ${accessToken}`,
  };

  return fetch(url, {
    ...options,
    headers,
  });
};

export default apiClient;
